import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import GmailAgentCodingBar from './GmailAgentCodingBar';
import './CommEmailPanel.css';

const API = '/api/opo/communications';

const LOCATION_OPTIONS = ['OptiPrime', 'OptiLaw', 'Optimum Realty'];

const INTERVAL_MS = [
  { label: '1 min', ms: 60000 },
  { label: '2 min', ms: 120000 },
  { label: '5 min', ms: 300000 },
  { label: '10 min', ms: 600000 },
  { label: '15 min', ms: 900000 },
];

/** Preferred Gmail action when rejecting a proposal (Reject-and-teach). */
const SUBAGENT_CORRECTED_ACTION_OPTIONS = [
  { value: 'archive', label: 'Archive (remove from Inbox)' },
  { value: 'star', label: 'Star' },
  { value: 'reply', label: 'Reply (draft if present, else LLM)' },
  { value: 'delete', label: 'Move to Trash' },
  { value: 'flag_urgent', label: 'Mark important (Gmail IMPORTANT)' },
  { value: 'no_action', label: 'No Gmail change (save teaching only)' },
];

function adminSessionHeaders() {
  try {
    const t = localStorage.getItem('sessionToken');
    return t ? { 'X-Admin-Session': t } : {};
  } catch {
    return {};
  }
}

function commFetch(url, options = {}) {
  const hasBody = options.body != null;
  return fetch(url, {
    cache: 'no-store',
    credentials: 'include',
    ...options,
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...adminSessionHeaders(),
      ...(options.headers || {}),
    },
  });
}

/**
 * Autonomy Speedometer — semicircular gauge (0–100%).
 * Colour sweeps from red (learning) through amber/yellow to green (autonomous).
 * The needle and percentage reflect how well the system has learned user patterns.
 */
function AutonomySpeedometer({ score = 0, totalDecisions = 0, totalAgreements = 0, patternCount = 0 }) {
  const pct = Math.round(Math.max(0, Math.min(1, score)) * 100);
  const r = 70;
  const cx = 90;
  const cy = 85;
  const startAngle = Math.PI;
  const sweepAngle = Math.PI;

  const arcPath = (startDeg, endDeg) => {
    const s = startAngle + (startDeg / 180) * Math.PI;
    const e = startAngle + (endDeg / 180) * Math.PI;
    const x1 = cx + r * Math.cos(s);
    const y1 = cy + r * Math.sin(s);
    const x2 = cx + r * Math.cos(e);
    const y2 = cy + r * Math.sin(e);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };

  const needleAngle = startAngle + (pct / 100) * sweepAngle;
  const needleLen = r - 12;
  const nx = cx + needleLen * Math.cos(needleAngle);
  const ny = cy + needleLen * Math.sin(needleAngle);

  let statusLabel = 'Learning';
  let statusColor = '#ef4444';
  if (pct >= 80) { statusLabel = 'Autonomous-ready'; statusColor = '#22c55e'; }
  else if (pct >= 60) { statusLabel = 'High confidence'; statusColor = '#4ade80'; }
  else if (pct >= 40) { statusLabel = 'Moderate'; statusColor = '#facc15'; }
  else if (pct >= 20) { statusLabel = 'Early patterns'; statusColor = '#fb923c'; }

  return (
    <div className="comm-email-speedometer">
      <svg width="180" height="110" viewBox="0 0 180 110">
        <path d={arcPath(0, 36)} stroke="#ef4444" strokeWidth="10" fill="none" strokeLinecap="round" />
        <path d={arcPath(36, 72)} stroke="#fb923c" strokeWidth="10" fill="none" strokeLinecap="round" />
        <path d={arcPath(72, 108)} stroke="#facc15" strokeWidth="10" fill="none" strokeLinecap="round" />
        <path d={arcPath(108, 144)} stroke="#4ade80" strokeWidth="10" fill="none" strokeLinecap="round" />
        <path d={arcPath(144, 180)} stroke="#22c55e" strokeWidth="10" fill="none" strokeLinecap="round" />

        <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#e2e8f0" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="4" fill="#e2e8f0" />

        <text x={cx} y={cy + 18} textAnchor="middle" fill={statusColor} fontSize="22" fontWeight="700">
          {pct}%
        </text>
      </svg>
      <div className="comm-email-speedometer-label" style={{ color: statusColor }}>{statusLabel}</div>
      <div className="comm-email-speedometer-stats">
        {totalDecisions} decisions · {totalAgreements} agreed · {patternCount} patterns
      </div>
    </div>
  );
}

function configMapFromRows(rows) {
  const m = {};
  (rows || []).forEach((r) => {
    m[r.config_key] = r.config_value;
  });
  return m;
}

/**
 * Email tab: Gmail Agent + Optimail Agent — Overview, Config, Briefing, Chat, Capabilities + coding bar.
 */
const CommEmailPanel = () => {
  const [agent, setAgent] = useState('gmail');
  const [subTab, setSubTab] = useState('overview');
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [capabilities, setCapabilities] = useState(null);
  const [models, setModels] = useState([]);
  const [ollamaModelsError, setOllamaModelsError] = useState(null);

  const [briefingLatest, setBriefingLatest] = useState(null);
  const [briefingList, setBriefingList] = useState([]);
  const [selectedBriefingId, setSelectedBriefingId] = useState('');
  const [generateLoading, setGenerateLoading] = useState(false);
  const [error, setError] = useState(null);
  const [configSaveLoading, setConfigSaveLoading] = useState(false);
  const [locationsSelected, setLocationsSelected] = useState(['OptiPrime']);

  const [formOllamaModel, setFormOllamaModel] = useState('');
  const [formCodingModel, setFormCodingModel] = useState('');
  const [formTemp, setFormTemp] = useState(0.2);
  const [formNumPredict, setFormNumPredict] = useState(2048);
  const [formIntervalMs, setFormIntervalMs] = useState(300000);
  const [formReconcileDays, setFormReconcileDays] = useState(120);
  const [formCatPrompt, setFormCatPrompt] = useState('');
  const [formSumPrompt, setFormSumPrompt] = useState('');
  const [formRefreshToken, setFormRefreshToken] = useState('');

  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');

  const [subagents, setSubagents] = useState([]);
  const [subagentsLoading, setSubagentsLoading] = useState(false);
  const [selectedSubagentId, setSelectedSubagentId] = useState('');
  const [subagentDetail, setSubagentDetail] = useState(null);
  const [subagentProposals, setSubagentProposals] = useState([]);
  const [subagentProposalFilter, setSubagentProposalFilter] = useState('pending');
  const [masterSubBriefing, setMasterSubBriefing] = useState(null);
  const [masterSubActions, setMasterSubActions] = useState([]);
  const [instructionPrefillKey, setInstructionPrefillKey] = useState(0);
  const [instructionPrefill, setInstructionPrefill] = useState('');
  const [emailActionContext, setEmailActionContext] = useState(null);
  /** Feedback on Sub-Agents tab (errors were only shown on Config/Briefing before). */
  const [subagentNotice, setSubagentNotice] = useState(null);
  const [proposalActionBusyId, setProposalActionBusyId] = useState(null);
  /** Proposal id with inline Reject-and-teach panel open */
  const [rejectTeachProposalId, setRejectTeachProposalId] = useState(null);
  const [rejectTeachAction, setRejectTeachAction] = useState('archive');
  const [rejectTeachReason, setRejectTeachReason] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const r = await commFetch(`${API}/status`);
      if (r.ok) setStatus(await r.json());
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      const r = await commFetch(`${API}/accounts`);
      if (r.ok) setAccounts(await r.json());
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadModels = useCallback(async () => {
    try {
      const r = await commFetch(`${API}/models`);
      const j = await r.json();
      setModels(j.models || []);
      setOllamaModelsError(j.error || null);
    } catch (e) {
      setModels([]);
      setOllamaModelsError(e.message);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    if (agent !== 'gmail') {
      return;
    }
    try {
      const r = await commFetch(`${API}/config?agent=gmail`);
      if (!r.ok) return;
      const j = await r.json();
      const m = configMapFromRows(j.config);
      setFormOllamaModel(m.ollama_model || '');
      setFormCodingModel(m.coding_model || '');
      setFormTemp(parseFloat(m.ollama_temperature) || 0.2);
      setFormNumPredict(parseInt(m.ollama_num_predict, 10) || 2048);
      setFormIntervalMs(parseInt(m.monitor_interval_ms, 10) || 300000);
      setFormReconcileDays(parseInt(m.reconcile_days, 10) || 120);
      setFormCatPrompt(m.categorization_prompt || '');
      setFormSumPrompt(m.summary_prompt || '');
      setFormRefreshToken('');
      try {
        const loc = JSON.parse(m.locations || '[]');
        if (Array.isArray(loc) && loc.length) setLocationsSelected(loc);
      } catch {
        /* keep default */
      }
    } catch (e) {
      console.error(e);
    }
  }, [agent]);

  const loadCapabilities = useCallback(async () => {
    try {
      const r = await commFetch(`${API}/capabilities?agent=${encodeURIComponent(agent)}`);
      if (r.ok) setCapabilities(await r.json());
    } catch (e) {
      console.error(e);
    }
  }, [agent]);

  const loadBriefings = useCallback(async () => {
    try {
      const r = await commFetch(`${API}/briefings/latest?agent=${encodeURIComponent(agent)}`);
      if (r.ok) {
        const j = await r.json();
        setBriefingLatest(j.briefing || null);
      }
      const r2 = await commFetch(`${API}/briefings?agent=${encodeURIComponent(agent)}&limit=30`);
      if (r2.ok) {
        const j2 = await r2.json();
        setBriefingList(j2.briefings || []);
      }
    } catch (e) {
      console.error(e);
    }
  }, [agent]);

  const loadChatHistory = useCallback(async () => {
    try {
      const r = await commFetch(`${API}/chat/history?agent=${encodeURIComponent(agent)}&limit=50`);
      if (r.ok) {
        const j = await r.json();
        setChatHistory(j.history || []);
      }
    } catch (e) {
      console.error(e);
    }
  }, [agent]);

  const loadSubagents = useCallback(async () => {
    if (agent !== 'gmail') return;
    setSubagentsLoading(true);
    try {
      const r = await commFetch(`${API}/subagents`);
      const j = await r.json();
      if (r.ok) setSubagents(j.subagents || []);
    } catch (e) {
      console.error(e);
    } finally {
      setSubagentsLoading(false);
    }
  }, [agent]);

  const loadSubagentDetail = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await commFetch(`${API}/subagents/${encodeURIComponent(id)}`);
      const j = await r.json();
      if (r.ok) setSubagentDetail(j);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadSubagentProposals = useCallback(async (id, status) => {
    if (!id) return;
    try {
      const q = status ? `?status=${encodeURIComponent(status)}` : '';
      const r = await commFetch(`${API}/subagents/${encodeURIComponent(id)}/proposals${q}`);
      const j = await r.json();
      if (r.ok) setSubagentProposals(j.proposals || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadMasterSubBriefing = useCallback(async () => {
    if (agent !== 'gmail') return;
    try {
      const r = await commFetch(`${API}/master-briefings/latest`);
      const j = await r.json();
      if (!r.ok) return;
      setMasterSubBriefing(j.briefing);
      let items = [];
      try {
        items = JSON.parse(j.briefing?.action_items || '[]');
        if (!Array.isArray(items)) items = [];
      } catch {
        items = [];
      }
      setMasterSubActions(items);
    } catch (e) {
      console.error(e);
    }
  }, [agent]);

  const openGmailInstructionBar = useCallback((text, ctx) => {
    setInstructionPrefill(text || '');
    setInstructionPrefillKey((k) => k + 1);
    setEmailActionContext(ctx || null);
  }, []);

  const decideSubagentProposal = async (subId, proposalId, decision, extraInstruction, teachPayload) => {
    setError(null);
    setSubagentNotice(null);
    setProposalActionBusyId(proposalId);
    try {
      const r = await commFetch(
        `${API}/subagents/${encodeURIComponent(subId)}/proposals/${proposalId}/decide`,
        {
          method: 'POST',
          body: JSON.stringify({
            decision,
            ...(decision === 'approve' && extraInstruction ? { instruction: extraInstruction } : {}),
            ...(decision === 'reject' && teachPayload?.corrected_action
              ? {
                  corrected_action: teachPayload.corrected_action,
                  reason: teachPayload.reason != null ? String(teachPayload.reason) : '',
                }
              : {}),
          }),
        }
      );
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || r.statusText);
      setSubagentNotice({
        type: 'ok',
        text: j.message || (decision === 'reject' ? 'Rejected — pattern updated.' : 'Saved.'),
      });
      if (decision === 'reject') {
        setRejectTeachProposalId(null);
        setRejectTeachReason('');
      }
      await loadSubagentDetail(subId);
      await loadSubagentProposals(subId, subagentProposalFilter);
      await loadSubagents();
      await loadMasterSubBriefing();
    } catch (e) {
      const msg = e.message || 'Decision failed';
      setSubagentNotice({ type: 'err', text: msg });
      setError(msg);
    } finally {
      setProposalActionBusyId(null);
    }
  };

  useEffect(() => {
    loadStatus();
    loadAccounts();
    loadModels();
    const t = setInterval(loadStatus, 60000);
    return () => clearInterval(t);
  }, [loadStatus, loadAccounts, loadModels]);

  useEffect(() => {
    loadCapabilities();
    loadBriefings();
    loadChatHistory();
    loadConfig();
  }, [loadCapabilities, loadBriefings, loadChatHistory, loadConfig]);

  useEffect(() => {
    if (agent === 'gmail' && subTab === 'subagents') {
      loadSubagents();
    }
  }, [agent, subTab, loadSubagents]);

  useEffect(() => {
    if (agent === 'gmail' && subTab === 'briefing') {
      loadMasterSubBriefing();
    }
  }, [agent, subTab, loadMasterSubBriefing]);

  useEffect(() => {
    if (agent === 'gmail' && subTab === 'subagents' && selectedSubagentId) {
      loadSubagentDetail(selectedSubagentId);
      loadSubagentProposals(selectedSubagentId, subagentProposalFilter);
    }
  }, [
    agent,
    subTab,
    selectedSubagentId,
    subagentProposalFilter,
    loadSubagentDetail,
    loadSubagentProposals,
  ]);

  useEffect(() => {
    if (agent === 'gmail' && subTab === 'subagents' && subagents.length && !selectedSubagentId) {
      setSelectedSubagentId(subagents[0].id);
    }
  }, [agent, subTab, subagents, selectedSubagentId]);

  useEffect(() => {
    setRejectTeachProposalId(null);
    setRejectTeachReason('');
  }, [selectedSubagentId]);

  const saveConfigPatch = async (updates) => {
    setConfigSaveLoading(true);
    setError(null);
    try {
      const r = await commFetch(`${API}/config`, {
        method: 'PUT',
        body: JSON.stringify({ agent: 'gmail', updates }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || r.statusText);
      await loadConfig();
      await loadStatus();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setConfigSaveLoading(false);
    }
  };

  const onSaveLocations = () => {
    saveConfigPatch([{ key: 'locations', value: JSON.stringify(locationsSelected) }]);
  };

  const onSaveAllConfig = () => {
    const updates = [
      { key: 'ollama_model', value: formOllamaModel },
      { key: 'coding_model', value: formCodingModel },
      { key: 'ollama_temperature', value: String(formTemp) },
      { key: 'ollama_num_predict', value: String(formNumPredict) },
      { key: 'monitor_interval_ms', value: String(formIntervalMs) },
      { key: 'reconcile_days', value: String(formReconcileDays) },
      { key: 'categorization_prompt', value: formCatPrompt },
      { key: 'summary_prompt', value: formSumPrompt },
    ];
    if (formRefreshToken.trim()) {
      updates.push({ key: 'gmail_refresh_token', value: formRefreshToken.trim() });
    }
    saveConfigPatch(updates);
  };

  const onGenerateBriefing = async (depth = 'full') => {
    setGenerateLoading(true);
    setError(null);
    try {
      const r = await commFetch(`${API}/briefings/generate`, {
        method: 'POST',
        body: JSON.stringify({ agent, depth }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || r.statusText);
      await loadBriefings();
    } catch (e) {
      setError(e.message || 'Generate failed');
    } finally {
      setGenerateLoading(false);
    }
  };

  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || streaming) return;
    setChatInput('');
    setStreaming(true);
    setStreamingMessage('');
    setChatHistory((prev) => [...prev, { role: 'user', content: msg, created_at: new Date().toISOString() }]);

    try {
      const res = await commFetch(`${API}/chat`, {
        method: 'POST',
        body: JSON.stringify({ message: msg, agent }),
      });
      if (!res.ok || !res.body) {
        setStreamingMessage('Error: ' + (res.statusText || 'No response body'));
        setStreaming(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        full += decoder.decode(value, { stream: true });
        setStreamingMessage(full);
      }
      setChatHistory((prev) => [...prev, { role: 'assistant', content: full, created_at: new Date().toISOString() }]);
      setStreamingMessage('');
    } catch (e) {
      setStreamingMessage('Error: ' + (e.message || 'Request failed'));
    } finally {
      setStreaming(false);
    }
  };

  const onChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChat();
    }
  };

  const onCodingConfigApplied = useCallback(async () => {
    await loadConfig();
    await loadStatus();
  }, [loadConfig, loadStatus]);

  const gmail = status?.gmail || {};
  const optimail = status?.optimail || {};
  const activeCard = agent === 'gmail' ? gmail : optimail;

  const selectedBriefing =
    selectedBriefingId && briefingList.length
      ? briefingList.find((b) => String(b.id) === String(selectedBriefingId))
      : null;
  const displayBriefing = selectedBriefingId && selectedBriefing ? selectedBriefing : briefingLatest;
  const displayMarkdown =
    displayBriefing?.summary_markdown || '_No briefing yet. Click **Generate briefing**._';

  const subTabs =
    agent === 'gmail'
      ? ['overview', 'config', 'briefing', 'subagents', 'chat', 'capabilities']
      : ['overview', 'config', 'briefing', 'chat', 'capabilities'];

  let masterSubSummaries = {};
  try {
    masterSubSummaries = JSON.parse(masterSubBriefing?.subagent_summaries || '{}');
    if (!masterSubSummaries || typeof masterSubSummaries !== 'object') masterSubSummaries = {};
  } catch {
    masterSubSummaries = {};
  }

  const gmailAccountList = accounts?.gmail || [];
  const optimailAccountList = accounts?.optimail || [];

  return (
    <div className="comm-email-panel">
      <aside className="comm-email-sidebar">
        <button
          type="button"
          className={`comm-email-agent-card${agent === 'gmail' ? ' comm-email-agent-card-active' : ''}`}
          onClick={() => setAgent('gmail')}
        >
          <h3>
            <span
              className="comm-email-status-dot"
              style={{
                backgroundColor: gmail.healthy ? '#10b981' : gmail.configured === false ? '#6b7280' : '#ef4444',
              }}
            />
            Gmail Agent
          </h3>
          <div className="comm-email-agent-meta">
            <div>{gmail.address || 'optiprimebc@gmail.com'}</div>
            <div>Mirrored: {gmail.mirrorCount ?? '—'}</div>
            <div>Last sync: {gmail.lastSync ? new Date(gmail.lastSync).toLocaleString() : '—'}</div>
          </div>
        </button>
        <button
          type="button"
          className={`comm-email-agent-card${agent === 'optimail' ? ' comm-email-agent-card-active' : ''}`}
          onClick={() => setAgent('optimail')}
        >
          <h3>
            <span
              className="comm-email-status-dot"
              style={{
                backgroundColor: optimail.healthy ? '#10b981' : optimail.configured === false ? '#6b7280' : '#ef4444',
              }}
            />
            Optimail Agent
          </h3>
          <div className="comm-email-agent-meta">
            <div>{optimail.address || 'contact@optiprime.ai'}</div>
            <div>Mirrored: {optimail.mirrorCount ?? '—'}</div>
            <div>Last sync: {optimail.lastSync ? new Date(optimail.lastSync).toLocaleString() : '—'}</div>
          </div>
        </button>
      </aside>

      <div className="comm-email-main">
        <div className="comm-email-subtabs">
          {subTabs.map((id) => (
            <button
              key={id}
              type="button"
              className={`comm-email-subtab${subTab === id ? ' comm-email-subtab-active' : ''}`}
              onClick={() => {
                setError(null);
                setSubTab(id);
              }}
            >
              {id === 'overview'
                ? 'Overview'
                : id === 'config'
                  ? 'Config'
                  : id === 'briefing'
                    ? 'Briefing'
                    : id === 'subagents'
                      ? 'Sub-Agents'
                      : id === 'chat'
                        ? 'Chat'
                        : 'Capabilities'}
            </button>
          ))}
        </div>

        <div className="comm-email-body-wrap">
          <div className="comm-email-body">
            {subTab === 'overview' && (
              <>
                <h4 className="comm-email-section-title">Linked accounts</h4>
                <p className="comm-email-hint" style={{ marginTop: 0 }}>
                  Accounts currently managed by this agent. One agent may support multiple mailboxes in the future.
                </p>
                {agent === 'gmail' &&
                  gmailAccountList.map((a) => (
                    <div key={a.id} className="comm-email-account-row">
                      <div>
                        <strong>{a.address}</strong>
                        <span className="comm-email-badge">{a.role}</span>
                      </div>
                      <div className="comm-email-agent-meta">
                        OAuth: {a.hasOAuth ? 'connected' : 'not detected'} · Last sync:{' '}
                        {a.lastSync ? new Date(a.lastSync).toLocaleString() : '—'}
                      </div>
                    </div>
                  ))}
                {agent === 'optimail' &&
                  optimailAccountList.map((a) => (
                    <div key={a.id} className="comm-email-account-row">
                      <div>
                        <strong>{a.address}</strong>
                        <span className="comm-email-badge">{a.role}</span>
                      </div>
                      <div className="comm-email-agent-meta">Configured: {a.configured ? 'yes' : 'no'}</div>
                    </div>
                  ))}

                <div className="comm-email-connect-row">
                  <button type="button" className="comm-email-btn" onClick={() => setConnectOpen(!connectOpen)}>
                    {connectOpen ? 'Hide ConnectMe' : 'Connect more (ConnectMe)'}
                  </button>
                  {accounts?.connectMeUrl && (
                    <a
                      className="comm-email-link"
                      href={accounts.connectMeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open in new tab
                    </a>
                  )}
                </div>
                {connectOpen && accounts?.connectMeUrl && (
                  <div className="comm-email-iframe-wrap">
                    <iframe
                      title="ConnectMe"
                      src={accounts.connectMeUrl}
                      className="comm-email-connect-iframe"
                      sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                    />
                  </div>
                )}

                {agent === 'gmail' && (
                  <>
                    <h4 className="comm-email-section-title">Locations</h4>
                    <p className="comm-email-hint">Where this agent is allowed to operate (stored in config).</p>
                    <div className="comm-email-locations">
                      {LOCATION_OPTIONS.map((loc) => (
                        <label key={loc} className="comm-email-check">
                          <input
                            type="checkbox"
                            checked={locationsSelected.includes(loc)}
                            onChange={(e) => {
                              if (e.target.checked) setLocationsSelected([...locationsSelected, loc]);
                              else setLocationsSelected(locationsSelected.filter((x) => x !== loc));
                            }}
                          />
                          {loc}
                        </label>
                      ))}
                    </div>
                    <button type="button" className="comm-email-btn" onClick={onSaveLocations} disabled={configSaveLoading}>
                      Save locations
                    </button>
                  </>
                )}
              </>
            )}

            {subTab === 'config' && agent === 'gmail' && (
              <>
                {error && <div className="comm-email-error">{error}</div>}
                <div className="comm-email-config-card">
                  <h4 className="comm-email-section-title">LLM (categorization)</h4>
                  <label className="comm-email-label">Model</label>
                  <select
                    className="comm-email-select comm-email-select-wide"
                    value={formOllamaModel}
                    onChange={(e) => setFormOllamaModel(e.target.value)}
                  >
                    <option value={formOllamaModel}>{formOllamaModel || '— pick or type below —'}</option>
                    {models.map((m) => (
                      <option key={m.name} value={m.name}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                  <input
                    className="comm-email-input"
                    placeholder="Or type exact Ollama model name"
                    value={formOllamaModel}
                    onChange={(e) => setFormOllamaModel(e.target.value)}
                  />
                  {ollamaModelsError && <p className="comm-email-hint">Ollama list: {ollamaModelsError}</p>}

                  <label className="comm-email-label">Coding agent model</label>
                  <select
                    className="comm-email-select comm-email-select-wide"
                    value={formCodingModel}
                    onChange={(e) => setFormCodingModel(e.target.value)}
                  >
                    <option value={formCodingModel}>{formCodingModel || '—'}</option>
                    {models.map((m) => (
                      <option key={m.name} value={m.name}>
                        {m.name}
                      </option>
                    ))}
                  </select>

                  <label className="comm-email-label">Temperature ({formTemp})</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={formTemp}
                    onChange={(e) => setFormTemp(parseFloat(e.target.value))}
                    className="comm-email-range"
                  />

                  <label className="comm-email-label">Max tokens (num_predict)</label>
                  <input
                    type="number"
                    className="comm-email-input"
                    min={256}
                    max={32768}
                    value={formNumPredict}
                    onChange={(e) => setFormNumPredict(parseInt(e.target.value, 10) || 2048)}
                  />
                </div>

                <div className="comm-email-config-card">
                  <h4 className="comm-email-section-title">Timing</h4>
                  <label className="comm-email-label">Monitor interval</label>
                  <select
                    className="comm-email-select comm-email-select-wide"
                    value={formIntervalMs}
                    onChange={(e) => setFormIntervalMs(parseInt(e.target.value, 10))}
                  >
                    {INTERVAL_MS.map((o) => (
                      <option key={o.ms} value={o.ms}>
                        {o.label}
                      </option>
                    ))}
                  </select>

                  <label className="comm-email-label">Reconcile inbox (days)</label>
                  <input
                    type="number"
                    className="comm-email-input"
                    min={14}
                    max={365}
                    value={formReconcileDays}
                    onChange={(e) => setFormReconcileDays(parseInt(e.target.value, 10) || 120)}
                  />
                </div>

                <div className="comm-email-config-card">
                  <h4 className="comm-email-section-title">Prompts</h4>
                  <p className="comm-email-hint">
                    Use placeholders:{' '}
                    <code>
                      {'{{GMAIL_ADDRESS}}, {{subject}}, {{senderName}}, {{bodyText2000}}, {{existingLabelsList}}'}
                    </code>{' '}
                    (categorization); <code>{'{{today}}, {{previousSummary}}, {{newSummariesText}}'}</code> (summary).
                  </p>
                  <label className="comm-email-label">Categorization prompt</label>
                  <textarea
                    className="comm-email-prompt"
                    value={formCatPrompt}
                    onChange={(e) => setFormCatPrompt(e.target.value)}
                    rows={12}
                  />
                  <label className="comm-email-label">Rolling summary prompt</label>
                  <textarea
                    className="comm-email-prompt"
                    value={formSumPrompt}
                    onChange={(e) => setFormSumPrompt(e.target.value)}
                    rows={10}
                  />
                </div>

                <div className="comm-email-config-card">
                  <h4 className="comm-email-section-title">Gmail refresh token (optional)</h4>
                  <p className="comm-email-hint">
                    Leave blank to keep using <code>node-opo-gmail-monitor/.env</code>. Set only to override for status
                    hints; the monitor still uses its process env.
                  </p>
                  <input
                    type="password"
                    className="comm-email-input"
                    placeholder="Paste new refresh token to store in DB"
                    value={formRefreshToken}
                    onChange={(e) => setFormRefreshToken(e.target.value)}
                    autoComplete="off"
                  />
                </div>

                <button
                  type="button"
                  className="comm-email-btn comm-email-btn-primary"
                  onClick={onSaveAllConfig}
                  disabled={configSaveLoading}
                >
                  {configSaveLoading ? 'Saving…' : 'Save all config'}
                </button>
              </>
            )}

            {subTab === 'config' && agent === 'optimail' && (
              <p className="comm-email-hint">Optimail agent config is managed via environment and Porkbun settings for now.</p>
            )}

            {subTab === 'briefing' && (
              <>
                <div className="comm-email-briefing-actions">
                  <button type="button" className="comm-email-btn" disabled={generateLoading} onClick={() => onGenerateBriefing('full')}>
                    {generateLoading ? 'Generating…' : 'Generate briefing'}
                  </button>
                  <button type="button" className="comm-email-btn" disabled={generateLoading} onClick={() => onGenerateBriefing('quick')}>
                    Quick scan
                  </button>
                  {briefingList.length > 0 && (
                    <select
                      className="comm-email-select"
                      value={selectedBriefingId}
                      onChange={(e) => setSelectedBriefingId(e.target.value)}
                    >
                      <option value="">Latest ({agent})</option>
                      {briefingList.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.briefing_date} {b.briefing_time} (#{b.id})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {error && subTab === 'briefing' && <div className="comm-email-error">{error}</div>}
                <div className="comm-email-markdown">
                  <ReactMarkdown>{displayMarkdown}</ReactMarkdown>
                </div>
                {displayBriefing && (
                  <p className="comm-email-hint">
                    Last run: {displayBriefing.created_at ? new Date(displayBriefing.created_at).toLocaleString() : '—'} · Threads:{' '}
                    {displayBriefing.threads_identified ?? '—'} · Emails analyzed: {displayBriefing.emails_analyzed ?? '—'}
                  </p>
                )}

                {agent === 'gmail' && (
                  <div className="comm-email-master-briefing-box">
                    <h4 className="comm-email-section-title" style={{ marginTop: 0 }}>
                      Sub-agent nightly master briefing
                    </h4>
                    <p className="comm-email-hint" style={{ marginTop: 0 }}>
                      Compiled after all label sub-agents finish their cycle. Action rows can pre-fill the Gmail instruction bar below.
                    </p>
                    {!masterSubBriefing && (
                      <p className="comm-email-hint">No master briefing in DB yet (runs after a full nightly cycle).</p>
                    )}
                    {masterSubBriefing && (
                      <>
                        <p className="comm-email-hint">
                          Date: <strong>{masterSubBriefing.briefing_date}</strong>
                          {masterSubBriefing.email_sent_at
                            ? ` · Emailed ${new Date(masterSubBriefing.email_sent_at).toLocaleString()}`
                            : ''}
                        </p>
                        <div className="comm-email-markdown">
                          <ReactMarkdown>{masterSubBriefing.summary_markdown || '_Empty_'}</ReactMarkdown>
                        </div>
                        {Object.keys(masterSubSummaries).length > 0 && (
                          <div style={{ marginTop: 16 }}>
                            <h5 className="comm-email-section-title">Per sub-agent summaries</h5>
                            {Object.entries(masterSubSummaries).map(([sid, text]) => (
                              <details key={sid} className="comm-email-cap-section" style={{ marginBottom: 8 }}>
                                <summary className="comm-email-cap-summary">{sid}</summary>
                                <div className="comm-email-cap-body">
                                  <ReactMarkdown>{String(text || '_No text_')}</ReactMarkdown>
                                </div>
                              </details>
                            ))}
                          </div>
                        )}
                        {masterSubActions.length > 0 && (
                          <div className="comm-email-action-items">
                            <h5 className="comm-email-section-title">Pending proposals / actions</h5>
                            {masterSubActions.map((a, idx) => (
                              <div key={idx} className="comm-email-action-item-row">
                                <span>
                                  <strong>#{a.proposal_id}</strong> [{a.subagent}] {a.type} ({a.priority}) — {a.summary}
                                </span>
                                <button
                                  type="button"
                                  className="comm-email-btn comm-email-btn-primary"
                                  onClick={() =>
                                    openGmailInstructionBar(
                                      `Act on proposal #${a.proposal_id} (${a.subagent}): ${a.summary}. Use draft if reply; otherwise follow best judgment.`,
                                      {
                                        subagentId: a.subagent,
                                        proposalId: a.proposal_id,
                                        emailIds: [],
                                      }
                                    )
                                  }
                                >
                                  Act
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}

            {subTab === 'subagents' && agent === 'gmail' && (
              <div className="comm-email-subagents-wrap">
                {subagentNotice && (
                  <div
                    className={
                      subagentNotice.type === 'ok'
                        ? 'comm-email-subagent-notice comm-email-subagent-notice-ok'
                        : 'comm-email-subagent-notice comm-email-subagent-notice-err'
                    }
                  >
                    {subagentNotice.text}
                  </div>
                )}
                <div className="comm-email-subagents-layout">
                <div className="comm-email-subagents-sidebar">
                  {subagentsLoading && <p className="comm-email-hint">Loading…</p>}
                  {!subagentsLoading &&
                    subagents.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className={`comm-email-subagent-item${selectedSubagentId === s.id ? ' comm-email-subagent-item-active' : ''}`}
                        onClick={() => setSelectedSubagentId(s.id)}
                      >
                        {s.display_name}
                        {Number(s.pending_proposals) > 0 && (
                          <span className="comm-email-subagent-badge">{s.pending_proposals}</span>
                        )}
                      </button>
                    ))}
                </div>
                <div className="comm-email-subagents-main">
                  {!selectedSubagentId && <p className="comm-email-hint">Select a sub-agent.</p>}
                  {selectedSubagentId && subagentDetail?.definition && (
                    <>
                      <div>
                        <h4 className="comm-email-section-title" style={{ marginTop: 0 }}>
                          {subagentDetail.definition.display_name}
                        </h4>
                        <p className="comm-email-hint" style={{ marginTop: 0 }}>
                          Label: <code>{subagentDetail.definition.gmail_label_name || '—'}</code>
                          {' · '}
                          Label ID:{' '}
                          {subagentDetail.definition.gmail_label_id ? (
                            <code>{subagentDetail.definition.gmail_label_id}</code>
                          ) : (
                            <em>unresolved (sync labels in gmail-monitor)</em>
                          )}
                          {' · '}
                          Status: {subagentDetail.definition.status}
                        </p>
                        {subagentDetail.cycles?.[0] && (
                          <p className="comm-email-hint">
                            Last cycle: {subagentDetail.cycles[0].cycle_date} — {subagentDetail.cycles[0].status}
                          </p>
                        )}
                      </div>

                      {subagentDetail.autonomy && (
                        <AutonomySpeedometer
                          score={subagentDetail.autonomy.score}
                          totalDecisions={subagentDetail.autonomy.total_decisions}
                          totalAgreements={subagentDetail.autonomy.total_agreements}
                          patternCount={subagentDetail.autonomy.pattern_count}
                        />
                      )}

                      <div className="comm-email-markdown" style={{ maxHeight: 280, overflow: 'auto' }}>
                        <ReactMarkdown>{subagentDetail.working_memory || '_Empty working memory._'}</ReactMarkdown>
                      </div>
                      <div className="comm-email-subagent-proposals">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <strong style={{ color: '#cbd5e1' }}>Proposals</strong>
                          <select
                            className="comm-email-select"
                            value={subagentProposalFilter}
                            onChange={(e) => setSubagentProposalFilter(e.target.value)}
                          >
                            <option value="">All</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="executed">Executed</option>
                          </select>
                          <button type="button" className="comm-email-btn" onClick={() => loadSubagents()}>
                            Refresh list
                          </button>
                        </div>
                        {subagentProposals.length === 0 && (
                          <p className="comm-email-hint">No proposals for this filter.</p>
                        )}
                        {subagentProposals.map((p) => (
                          <div key={p.id} className="comm-email-proposal-block">
                            <div className="comm-email-proposal-row">
                              <span style={{ flex: '1 1 200px' }}>
                                <strong>#{p.id}</strong> {p.proposal_type} ({p.priority}) — {p.proposal_summary}
                              </span>
                              {p.status === 'pending' && (
                                <>
                                  <button
                                    type="button"
                                    className="comm-email-btn comm-email-btn-primary"
                                    disabled={proposalActionBusyId === p.id}
                                    onClick={() => {
                                      setRejectTeachProposalId(null);
                                      decideSubagentProposal(selectedSubagentId, p.id, 'approve');
                                    }}
                                  >
                                    {proposalActionBusyId === p.id ? 'Saving…' : 'Approve'}
                                  </button>
                                  <button
                                    type="button"
                                    className="comm-email-btn"
                                    disabled={proposalActionBusyId === p.id}
                                    onClick={() => {
                                      if (rejectTeachProposalId === p.id) {
                                        setRejectTeachProposalId(null);
                                      } else {
                                        setRejectTeachProposalId(p.id);
                                        setRejectTeachAction('archive');
                                        setRejectTeachReason('');
                                      }
                                    }}
                                  >
                                    {rejectTeachProposalId === p.id ? 'Close' : 'Reject…'}
                                  </button>
                                  <button
                                    type="button"
                                    className="comm-email-btn"
                                    onClick={() => {
                                      setSubagentNotice({
                                        type: 'ok',
                                        text:
                                          'Bar opened below — for one-off Gmail actions use "Queue Gmail instruction". To save preferred behaviour for similar mail, use Reject… → Reject & teach.',
                                      });
                                      openGmailInstructionBar(
                                        `Sub-agent ${selectedSubagentId}, proposal #${p.id}: ${p.proposal_summary}. Refine and queue when ready.`,
                                        {
                                          subagentId: selectedSubagentId,
                                          proposalId: p.id,
                                          emailIds: p.email_id ? [p.email_id] : [],
                                        }
                                      );
                                    }}
                                  >
                                    Instruct
                                  </button>
                                </>
                              )}
                              {p.status !== 'pending' && (
                                <span className="comm-email-hint">{p.status}</span>
                              )}
                            </div>
                            {p.status === 'pending' && rejectTeachProposalId === p.id && (
                              <div className="comm-reject-teach-panel">
                                <p className="comm-reject-teach-intro">
                                  <strong>Reject and teach:</strong> the model recommended{' '}
                                  <code>{p.proposal_type}</code>. Choose what you want instead and why — this is
                                  saved to <strong>Learned User Patterns</strong> for future similar emails.
                                </p>
                                <label className="comm-reject-teach-label" htmlFor={`reject-action-${p.id}`}>
                                  Instead I want
                                </label>
                                <select
                                  id={`reject-action-${p.id}`}
                                  className="comm-email-select comm-reject-teach-select"
                                  value={rejectTeachAction}
                                  onChange={(e) => setRejectTeachAction(e.target.value)}
                                >
                                  {SUBAGENT_CORRECTED_ACTION_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                                <label className="comm-reject-teach-label" htmlFor={`reject-reason-${p.id}`}>
                                  Why? (saved for the sub-agent; helps match similar mail)
                                </label>
                                <textarea
                                  id={`reject-reason-${p.id}`}
                                  className="comm-reject-teach-textarea"
                                  rows={4}
                                  value={rejectTeachReason}
                                  onChange={(e) => setRejectTeachReason(e.target.value)}
                                  placeholder="e.g. Autodeposit notices from Interac are routine — archive them, do not star."
                                />
                                <div className="comm-reject-teach-actions">
                                  <button
                                    type="button"
                                    className="comm-email-btn"
                                    onClick={() => setRejectTeachProposalId(null)}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className="comm-email-btn comm-email-btn-primary"
                                    disabled={proposalActionBusyId === p.id}
                                    onClick={() =>
                                      decideSubagentProposal(selectedSubagentId, p.id, 'reject', null, {
                                        corrected_action: rejectTeachAction,
                                        reason: rejectTeachReason,
                                      })
                                    }
                                  >
                                    {proposalActionBusyId === p.id ? 'Saving…' : 'Reject & teach'}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                </div>
              </div>
            )}

            {subTab === 'subagents' && agent !== 'gmail' && (
              <p className="comm-email-hint">Sub-agents apply to the Gmail agent only.</p>
            )}

            {subTab === 'chat' && (
              <>
                <p className="comm-email-hint" style={{ marginTop: 0 }}>
                  PA for <strong>{activeCard.address}</strong>. Ask about recent mail or give instructions. Destructive actions run via{' '}
                  <code style={{ color: '#94a3b8' }}>POST /api/opo/communications/execute</code>.
                </p>
                <div className="comm-email-chat-history">
                  {chatHistory.map((h, i) => (
                    <div
                      key={i}
                      className={`comm-email-chat-bubble ${
                        h.role === 'user' ? 'comm-email-chat-bubble-user' : 'comm-email-chat-bubble-assistant'
                      }`}
                    >
                      {h.content}
                    </div>
                  ))}
                  {streaming && streamingMessage && (
                    <div className="comm-email-chat-bubble comm-email-chat-bubble-assistant">{streamingMessage}</div>
                  )}
                </div>
                <div className="comm-email-chat-input-row">
                  <textarea
                    className="comm-email-chat-input"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={onChatKeyDown}
                    placeholder="Ask about emails or describe what you want done…"
                    disabled={streaming}
                  />
                  <button type="button" className="comm-email-btn" onClick={sendChat} disabled={streaming || !chatInput.trim()}>
                    Send
                  </button>
                </div>
              </>
            )}

            {subTab === 'capabilities' && capabilities && (
              <>
                <p style={{ marginTop: 0, color: '#94a3b8', fontSize: '0.875rem' }}>
                  Agent: <strong style={{ color: '#e2e8f0' }}>{capabilities.agent}</strong>
                </p>
                {(capabilities.capabilities || []).map((cap, idx) => (
                  <details key={idx} className="comm-email-cap-section" open={idx < 3}>
                    <summary className="comm-email-cap-summary">{cap.title}</summary>
                    <div className="comm-email-cap-body">
                      <div>Status: {cap.status}</div>
                      {cap.detail && <div style={{ marginTop: 8 }}>{cap.detail}</div>}
                      {cap.rules && cap.rules.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <strong style={{ color: '#cbd5e1' }}>Rules / instructions</strong>
                          {cap.rules.map((r, j) => (
                            <div key={j} className="comm-email-cap-rule">
                              {r}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </details>
                ))}
              </>
            )}

            {subTab === 'capabilities' && !capabilities && <p className="comm-email-hint">Loading capabilities…</p>}
          </div>

          {agent === 'gmail' && (
            <GmailAgentCodingBar
              variant="inline"
              onConfigApplied={onCodingConfigApplied}
              instructionPrefillKey={instructionPrefillKey}
              instructionPrefill={instructionPrefill}
              emailActionContext={emailActionContext}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CommEmailPanel;
